import { useInfiniteQuery } from '@tanstack/react-query';
import { Search } from '@domain/insights/models/Search';
import { container } from '@infrastructure/container';
import { PaginatedResource } from '@domain/base/Resource';

interface UseSearchTermsOptions {
  connectorTypes?: string[];
  searchTerm?: string;
  pageSize?: number;
}

export const useSearchTerms = (options?: UseSearchTermsOptions) => {
  const pageSize = options?.pageSize || 20;

  const {
    data,
    isLoading: isLoadingSearchTerms,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error: searchTermsError
  } = useInfiniteQuery<PaginatedResource<Search[]>>({
    queryKey: ['searchTerms', options?.connectorTypes, options?.searchTerm, pageSize],
    queryFn: async ({ pageParam = 1 }) => {
      const result = await container.insightsRepository.getSearchTerms(
        options?.connectorTypes,
        options?.searchTerm,
        { page: pageParam, limit: pageSize }
      );
      if (result.status === 'error') throw new Error(result.error.message);
      return result;
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.status === 'error') return undefined;
      return lastPage.hasNext ? lastPage.page + 1 : undefined;
    },
    initialPageParam: 1,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
  });

  // Flatten all pages into a single array
  const searchTerms = data?.pages
    .filter(page => page.status === 'success')
    .flatMap(page => page.data) ?? [];

  return {
    searchTerms,
    isLoading: isLoadingSearchTerms,
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
    error: searchTermsError
  };
};

